import {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import info from '~/assets/icons/info_2.svg';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Divider } from '~/components/ui/Divider';
import {
	NumberplatePreview,
	NumberPlatePreviewSize,
} from '~/components/ui/NumberplatePreview';
import { Text } from '~/components/ui/Text';
import { useZoomFactor } from '~/hooks';
import { IStorageNumberPlate, rage, useObservable } from '~/rage';
import { ModalButtonColor, ModalType, numWord, WalletType } from '~/shared';
import { hintTextByType } from './StoragePlate.model';
import {
	StyledButtonsGrid,
	StyledContainer,
	StyledHint,
	StyledStorageItemTopContainer,
	StyledTimeContainer,
} from './StoragePlate.styled';
interface IStoragePlateProps {
	numberPlate: IStorageNumberPlate;
}

const StoragePlate: React.FC<IStoragePlateProps> = ({
	numberPlate,
}: IStoragePlateProps) => {
	const localNumberPlate = useObservable(
		rage.vehicleNumberPlates.getLocalOwnedVehicleNumberPlate(),
	);
	const [hintVisible, setHintVisible] = useState(false);
	const [hintPosition, setHintPosition] = useState({ top: 0, left: 0 });
	const infoButtonRef = useRef<HTMLDivElement>(null);
	const zoomFactor = useZoomFactor();

	const days = useMemo(
		() =>
			Math.floor(
				(new Date(numberPlate.inStorageExpiresAt!).getTime() -
					new Date().getTime()) /
					(24 * 3600 * 1000),
			),
		[numberPlate],
	);

	useEffect(() => {
		if (!infoButtonRef.current) return;

		const updatePosition = () => {
			const rect = infoButtonRef.current!.getBoundingClientRect();
			const top = (rect.top - 50) / zoomFactor;
			const left = (rect.left + rect.width / 2) / zoomFactor;

			setHintPosition({ top, left });
		};

		updatePosition();

		window.addEventListener('resize', updatePosition);
		window.addEventListener('scroll', updatePosition, true);

		return () => {
			window.removeEventListener('resize', updatePosition);
			window.removeEventListener('scroll', updatePosition, true);
		};
	}, [zoomFactor, hintVisible]);

	const handleReplace = useCallback(() => {
		const storePrice = rage.vehicleNumberPlates.getStoreNumberPlatePrice();
		let localPlateName = `${localNumberPlate?.text ?? ''}${
			localNumberPlate?.region ?? ''
		}`;
		if (localPlateName.length === 0) {
			localPlateName = '???';
		}

		let plateName = `${numberPlate?.text ?? ''}${numberPlate?.region ?? ''}`;
		if (plateName.length === 0) {
			plateName = '???';
		}

		rage.modal.showModal(
			{
				type: ModalType.Price,
				title: 'Подтверждение',
				text: `Вы действительно хотите сохранить номер ${localPlateName} и заменить на ${plateName}?`,
				price: storePrice,
				wallet: WalletType.Donate,
				acceptButton: { text: 'Оплатить', color: ModalButtonColor.Success },
				cancelButton: {
					text: 'Отменить',
					color: ModalButtonColor.Secondary,
				},
			},
			(result) => {
				if (result.isAccepted) {
					rage.vehicleNumberPlates
						.moveNumberPlateFromStorage(numberPlate, false)
						.catch(console.error);
				}
			},
		);
	}, [numberPlate, localNumberPlate]);

	const handleSetup = useCallback(() => {
		const storePrice = rage.vehicleNumberPlates.getStoreNumberPlatePrice();
		let localPlateName = `${localNumberPlate?.text ?? ''}${
			localNumberPlate?.region ?? ''
		}`;
		if (localPlateName.length === 0) {
			localPlateName = '???';
		}

		let plateName = `${numberPlate?.text ?? ''}${numberPlate?.region ?? ''}`;
		if (plateName.length === 0) {
			plateName = '???';
		}

		rage.modal.showModal(
			{
				type: ModalType.Price,
				title: 'Подтверждение',
				text: `Вы действительно хотите удалить номер ${localPlateName} и установить ${plateName}?`,
				price: storePrice,
				wallet: WalletType.Donate,
				acceptButton: { text: 'Установить', color: ModalButtonColor.Success },
				cancelButton: {
					text: 'Отменить',
					color: ModalButtonColor.Secondary,
				},
			},
			(result) => {
				if (result.isAccepted) {
					rage.vehicleNumberPlates
						.moveNumberPlateFromStorage(numberPlate, true)
						.catch((err) => rage.notifications.showException(err));
				}
			},
		);
	}, [numberPlate, localNumberPlate]);

	return (
		<Fragment>
			<StyledContainer>
				<StyledStorageItemTopContainer>
					<NumberplatePreview
						numberPlate={numberPlate}
						size={NumberPlatePreviewSize.Size30}
					/>
					<div ref={infoButtonRef}>
						<Button
							color={ButtonColors.Default}
							height={ButtonHeight.Height30}
							square
							onMouseEnter={() => setHintVisible(true)}
							onMouseLeave={() => setHintVisible(false)}
						>
							<img src={info} width={14} height={14} />
						</Button>
					</div>
					<StyledTimeContainer>
						<Text style={{ opacity: 0.25 }}>Осталось:</Text>
						<Text>
							{days} <span>{numWord(days, ['день', 'дня', 'дней'])}</span>
						</Text>
						<div className="icon" />
					</StyledTimeContainer>
				</StyledStorageItemTopContainer>
				<Divider $direction="H" style={{ opacity: 0.1 }} />
				{numberPlate.onModeration ? (
					<Button
						color={ButtonColors.Default}
						height={ButtonHeight.Height40}
						disabled
					>
						<Text>Номер находится на модерации</Text>
					</Button>
				) : (
					<StyledButtonsGrid>
						<Button
							color={ButtonColors.Buy}
							height={ButtonHeight.Height40}
							onClick={handleReplace}
						>
							<Text>Заменить номер</Text>
						</Button>
						<Button
							color={ButtonColors.Select2}
							height={ButtonHeight.Height40}
							onClick={handleSetup}
						>
							<Text>Установить номер</Text>
						</Button>
					</StyledButtonsGrid>
				)}
			</StyledContainer>
			{createPortal(
				<StyledHint
					style={{
						top: `${hintPosition.top}px`,
						left: `${hintPosition.left}px`,
						opacity: hintVisible ? 1 : 0,
						pointerEvents: hintVisible ? 'auto' : 'none',
					}}
				>
					<Text>{hintTextByType[numberPlate.type]}</Text>
				</StyledHint>,
				document.body,
			)}
		</Fragment>
	);
};

export { StoragePlate };
